<template>
  <div>
    <Nav></Nav>
    <b-container fluid>
      <b-row>
        <b-col>
          <h1>Orden de Compra Sugerida</h1>
          <p class="text-muted">Calcula las cantidades sugeridas de compra basado en consumo historico y proyecciones</p>
        </b-col>
      </b-row>

      <!-- Seleccion de Parametros -->
      <b-row>
        <b-col lg="2" md="6" sm="12">
          <b-form-group label="Sucursal:">
            <vu-select
              v-model="selectedSucursal"
              :options="sucursals"
              :reduce="option => option.value"
              label="text"
              placeholder="Seleccionar Sucursal">
            </vu-select>
          </b-form-group>
        </b-col>
        <b-col lg="2" md="6" sm="12">
          <b-form-group label="Fecha de Entrega (PO):">
            <b-form-input
              type="date"
              v-model="poDate"
              :min="minDate">
            </b-form-input>
          </b-form-group>
        </b-col>
        <b-col lg="2" md="6" sm="12">
          <b-form-group label="Fecha Fin Cobertura:">
            <b-form-input
              type="date"
              v-model="poEndDate"
              :min="poDate">
            </b-form-input>
          </b-form-group>
        </b-col>
        <b-col lg="2" md="6" sm="12">
          <b-form-group label="Semanas de Referencia:">
            <b-form-input
              type="number"
              v-model.number="weeksBack"
              min="1"
              max="52"
              placeholder="Ej: 4">
            </b-form-input>
            <small class="text-muted">Semanas atras para referencia de consumo</small>
          </b-form-group>
        </b-col>
        <b-col lg="2" md="6" sm="12">
          <b-form-group label="Lista de Insumos:">
            <vu-select
              v-model="selectedShoppingList"
              :options="shoppingLists"
              :reduce="option => option.value"
              label="text"
              placeholder="Todos los insumos">
            </vu-select>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row class="mb-3">
        <b-col sm="12" class="d-flex justify-content-end">
          <b-button variant="primary" @click="generateReport" :disabled="!canGenerateReport">
            Generar Reporte
          </b-button>
          <b-button variant="success" class="ml-2" @click="exportToExcel" :disabled="!reportData.length">
            Exportar a Excel
          </b-button>
        </b-col>
      </b-row>

      <!-- Resumen del Reporte -->
      <b-row v-if="reportData.length" class="mb-3">
        <b-col sm="12">
          <b-card bg-variant="light">
            <b-row>
              <b-col md="3" sm="6">
                <div class="summary-item">
                  <div class="summary-label">Insumos con Sugerencia:</div>
                  <div class="summary-value">{{ itemsWithSuggestion }}</div>
                </div>
              </b-col>
              <b-col md="3" sm="6">
                <div class="summary-item">
                  <div class="summary-label">Dias de Cobertura:</div>
                  <div class="summary-value">{{ coverageDays }} dias</div>
                </div>
              </b-col>
              <b-col md="3" sm="6">
                <div class="summary-item">
                  <div class="summary-label">Valor Estimado Compra:</div>
                  <div class="summary-value">${{ formatNumber(totalSuggestedValue) }}</div>
                </div>
              </b-col>
              <b-col md="3" sm="6">
                <div class="summary-item">
                  <div class="summary-label">Periodo Referencia:</div>
                  <div class="summary-value">{{ referenceStartDate }} - {{ referenceEndDate }}</div>
                </div>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
      </b-row>

      <!-- Controles de Filtro -->
      <b-row v-if="reportData.length" class="mb-3">
        <b-col md="4" sm="12">
          <b-form-group label="Buscar:">
            <b-form-input v-model="filter" placeholder="Buscar por nombre, categoria, etc."></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="3" sm="6">
          <b-form-group label="Solo con sugerencia > 0:">
            <div class="d-flex align-items-center mt-2">
              <b-form-checkbox v-model="showOnlySuggested" switch>
                {{ showOnlySuggested ? 'Si' : 'No' }}
              </b-form-checkbox>
            </div>
          </b-form-group>
        </b-col>
        <b-col md="2" sm="6">
          <b-form-group label="Elementos por pagina:">
            <b-form-select v-model="perPage" :options="[10, 25, 50, 100]"></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Tabla del Reporte -->
      <b-row>
        <b-col sm="12">
          <b-overlay :show="loading" rounded="sm">
            <b-table v-if="reportData.length" :items="filteredReportData" :fields="fields" :per-page="perPage"
              :current-page="currentPage" :filter="filter" striped hover responsive show-empty small stacked="md"
              :sort-by.sync="sortBy" :sort-desc.sync="sortDesc">

              <template #cell(supply_price)="data">
                ${{ formatNumber(data.value) }}
              </template>

              <template #cell(last_count_qty)="data">
                {{ formatNumber(data.value) }} {{ data.item.supply_unit }}
              </template>

              <template #cell(pending_movements_qty)="data">
                <span :class="{ 'text-success': data.value > 0, 'text-danger': data.value < 0 }">
                  {{ formatNumber(data.value) }} {{ data.item.supply_unit }}
                </span>
              </template>

              <template #cell(current_projected_qty)="data">
                <span :class="{ 'text-danger': data.value < 0 }">
                  {{ formatNumber(data.value) }} {{ data.item.supply_unit }}
                </span>
              </template>

              <template #cell(historical_consumption)="data">
                {{ formatNumber(data.value) }} {{ data.item.supply_unit }}
              </template>

              <template #cell(projected_consumption)="data">
                {{ formatNumber(data.value) }} {{ data.item.supply_unit }}
              </template>

              <template #cell(projected_qty_at_po_date)="data">
                <span :class="{ 'text-danger': data.value <= 0 }">
                  {{ formatNumber(data.value) }} {{ data.item.supply_unit }}
                </span>
              </template>

              <template #cell(suggested_po_qty)="data">
                <span class="font-weight-bold" :class="{ 'text-primary': data.value > 0 }">
                  {{ formatNumber(data.value) }} {{ data.item.supply_unit }}
                </span>
              </template>

              <template #cell(suggested_value)="data">
                <span class="font-weight-bold" :class="{ 'text-primary': data.value > 0 }">
                  ${{ formatNumber(data.item.suggested_po_qty * data.item.supply_price) }}
                </span>
              </template>
            </b-table>

            <div v-if="!reportData.length && !loading" class="text-center p-5">
              <p>No hay datos disponibles. Por favor seleccione parametros y genere un reporte.</p>
            </div>
          </b-overlay>
        </b-col>
      </b-row>

      <!-- Paginacion -->
      <b-row v-if="reportData.length">
        <b-col sm="12" class="mt-3">
          <b-pagination v-model="currentPage" :total-rows="filteredReportData.length" :per-page="perPage" align="center">
          </b-pagination>
        </b-col>
      </b-row>

    </b-container>
  </div>
</template>

<script>
import Nav from "../components/Nav.vue";
import axios from "axios";
import * as XLSX from 'xlsx';

export default {
  name: "SuggestedPOReport",
  components: { Nav },
  data() {
    return {
      // Parametros de seleccion
      selectedSucursal: null,
      poDate: "",
      poEndDate: "",
      weeksBack: 4,
      selectedShoppingList: null,

      // Fuentes de datos
      sucursals: [],
      shoppingLists: [],
      reportData: [],

      // Controles de tabla
      filter: "",
      perPage: 25,
      currentPage: 1,
      sortBy: 'suggested_po_qty',
      sortDesc: true,
      showOnlySuggested: true,

      // Estado de UI
      loading: false,

      // Definiciones de campos de la tabla
      fields: [
        { key: 'supply_name', label: 'Insumo', sortable: true },
        { key: 'supply_category', label: 'Categoria', sortable: true },
        { key: 'supply_unit', label: 'Unidad', sortable: true },
        { key: 'supply_price', label: 'Precio', sortable: true },
        { key: 'last_count_qty', label: 'Ultimo Conteo', sortable: true },
        { key: 'last_count_date', label: 'Fecha Conteo', sortable: true },
        { key: 'pending_movements_qty', label: 'Mov. Pendientes', sortable: true },
        { key: 'current_projected_qty', label: 'Inv. Actual', sortable: true },
        { key: 'historical_consumption', label: 'Consumo Hist.', sortable: true },
        { key: 'projected_qty_at_po_date', label: 'Inv. al Recibir', sortable: true },
        { key: 'suggested_po_qty', label: 'Cant. Sugerida', sortable: true },
        { key: 'suggested_value', label: 'Valor Sugerido', sortable: true }
      ]
    };
  },
  computed: {
    minDate() {
      return new Date().toISOString().split('T')[0];
    },
    canGenerateReport() {
      return this.selectedSucursal && this.poDate && this.poEndDate && this.weeksBack > 0;
    },
    filteredReportData() {
      if (!this.showOnlySuggested) {
        return this.reportData;
      }
      return this.reportData.filter(item => item.suggested_po_qty > 0);
    },
    itemsWithSuggestion() {
      return this.reportData.filter(item => item.suggested_po_qty > 0).length;
    },
    coverageDays() {
      if (!this.poDate || !this.poEndDate) return 0;
      const start = new Date(this.poDate);
      const end = new Date(this.poEndDate);
      return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    },
    totalSuggestedValue() {
      return this.reportData.reduce((sum, item) => sum + (item.suggested_po_qty * item.supply_price), 0);
    },
    referenceStartDate() {
      if (!this.poDate || !this.weeksBack) return '';
      const date = new Date(this.poDate);
      date.setDate(date.getDate() - (this.weeksBack * 7));
      return date.toISOString().split('T')[0];
    },
    referenceEndDate() {
      if (!this.poEndDate || !this.weeksBack) return '';
      const date = new Date(this.poEndDate);
      date.setDate(date.getDate() - (this.weeksBack * 7));
      return date.toISOString().split('T')[0];
    }
  },
  mounted() {
    this.loadSucursals();
    this.loadShoppingLists();
    this.setDefaultDates();
  },
  methods: {
    setDefaultDates() {
      const today = new Date();
      const nextWeek = new Date(today);
      nextWeek.setDate(nextWeek.getDate() + 7);
      const twoWeeks = new Date(today);
      twoWeeks.setDate(twoWeeks.getDate() + 14);

      this.poDate = nextWeek.toISOString().split('T')[0];
      this.poEndDate = twoWeeks.toISOString().split('T')[0];
    },
    loadSucursals() {
      axios.get("/sucursals")
        .then(response => {
          this.sucursals = response.data
            .sort((a, b) => a.nombre.localeCompare(b.nombre))
            .map(sucursal => ({
              value: sucursal.id,
              text: sucursal.nombre
            }));
        })
        .catch(error => {
          this.makeToast("Error", "Error al cargar sucursales: " + error.message, "danger");
        });
    },
    loadShoppingLists() {
      axios.get("/supply/shoppinglists")
        .then(response => {
          this.shoppingLists = [
            { value: 0, text: "Todos los insumos" },
            ...response.data.map(list => ({
              value: list.id,
              text: list.name
            }))
          ];
        })
        .catch(error => {
          console.log("Error al cargar listas de insumos:", error.message);
          this.shoppingLists = [{ value: 0, text: "Todos los insumos" }];
        });
    },
    async generateReport() {
      if (!this.canGenerateReport) return;

      this.loading = true;

      try {
        const response = await axios.post("/report/inventory/suggested-po", {
          sucursal_id: this.selectedSucursal,
          po_date: this.poDate,
          po_end_date: this.poEndDate,
          shopping_list_id: this.selectedShoppingList || 0,
          weeks_back: this.weeksBack
        });

        if (response.data == null) {
          this.reportData = [];
        } else {
          this.reportData = response.data;
        }

        this.currentPage = 1;
        this.loading = false;
      } catch (error) {
        this.makeToast("Error", "Error al generar reporte: " + error.message, "danger");
        this.loading = false;
      }
    },
    exportToExcel() {
      if (!this.reportData.length) return;

      const sucursalName = this.sucursals.find(s => s.value === this.selectedSucursal)?.text || 'Sucursal';

      const excelData = this.filteredReportData.map(item => ({
        'ID Insumo': item.supply_id,
        'Nombre Insumo': item.supply_name,
        'Categoria': item.supply_category,
        'Unidad': item.supply_unit,
        'Precio Unitario': item.supply_price,
        'Ultimo Conteo': item.last_count_qty,
        'Fecha Conteo': item.last_count_date,
        'Mov. Pendientes': item.pending_movements_qty,
        'Inventario Actual': item.current_projected_qty,
        'Consumo Historico': item.historical_consumption,
        'Inv. al Recibir PO': item.projected_qty_at_po_date,
        'Cantidad Sugerida': item.suggested_po_qty,
        'Valor Sugerido': item.suggested_po_qty * item.supply_price,
        'Sucursal': sucursalName
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const range = XLSX.utils.decode_range(worksheet['!ref']);

      const columnWidths = [
        { wch: 10 },  // ID
        { wch: 30 },  // Nombre
        { wch: 15 },  // Categoria
        { wch: 10 },  // Unidad
        { wch: 12 },  // Precio
        { wch: 12 },  // Ultimo Conteo
        { wch: 12 },  // Fecha Conteo
        { wch: 14 },  // Mov Pendientes
        { wch: 14 },  // Inv Actual
        { wch: 15 },  // Consumo Hist
        { wch: 14 },  // Inv al Recibir
        { wch: 14 },  // Cant Sugerida
        { wch: 14 },  // Valor Sugerido
        { wch: 20 }   // Sucursal
      ];
      worksheet['!cols'] = columnWidths;
      worksheet['!autofilter'] = { ref: XLSX.utils.encode_range(range) };

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Orden Compra Sugerida");

      const fileName = `Orden_Compra_Sugerida_${sucursalName}_${this.poDate}_a_${this.poEndDate}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    },
    formatNumber(value) {
      if (value === null || value === undefined) return '0';
      return Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    makeToast(title, content, variant) {
      this.$bvToast.toast(content, {
        title,
        variant,
        autoHideDelay: 5000,
        appendToast: true,
      });
    }
  }
};
</script>

<style scoped>
.summary-item {
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 5px;
  margin-bottom: 10px;
}

.summary-label {
  font-weight: bold;
  font-size: 0.9rem;
  color: #495057;
}

.summary-value {
  font-size: 1.2rem;
  font-weight: 600;
  color: #343a40;
}

@media (max-width: 768px) {
  .b-table-stacked-md th {
    display: none;
  }
}
</style>
